import type { ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";

import { extractSubtitle, stripMarkdownBold } from "./AiSearchResultCard.utils";
import { buildAppliedFacets } from "./appliedFacets";
import { getEntityMeta } from "./entityMeta";
import { highlightMatches } from "./highlight";
import type { ICategorizedHit } from "./types";

interface IAiSearchResultCardProps {
    hit: ICategorizedHit;
    query: string;
    isHighlighted: boolean;
    isPending: boolean;
    elementId: string;
    onClick: () => void;
    /**
     * graft/A6.4: render the applied-filter facets and make them clickable.
     * Opt-in because the 640px dropdown has no room for them — it stays
     * pill-free until A6.6 retires it, which also keeps its visual baselines
     * untouched by this stage.
     */
    showFacets?: boolean;
    /** Narrow the list to hits carrying this facet. */
    onFacetClick?: (facetKey: string) => void;
}

function extractTitle(hit: ICategorizedHit): string {
    if (hit.summary) {
        const firstLine = hit.summary.split("\n")[0].trim();
        if (firstLine.length > 0) {
            const cleaned = stripMarkdownBold(firstLine);
            if (cleaned.trim().length > 0) return cleaned;
        }
    }
    const meta = getEntityMeta(hit.entity);
    return `${meta.label} #${hit.id}`;
}

export function AiSearchResultCard(props: IAiSearchResultCardProps) {
    const {
        hit,
        query,
        isHighlighted,
        isPending,
        elementId,
        onClick,
        showFacets = false,
        onFacetClick,
    } = props;
    const meta = getEntityMeta(hit.entity);
    const title = extractTitle(hit);
    const subtitle = extractSubtitle(hit);
    const facets = showFacets ? buildAppliedFacets(hit) : [];

    const titleContent: ReactNode = query ? highlightMatches(title, query) : title;

    return (
        <div
            className={cn("ai-search-result-card", {
                "ai-search-result-card--highlighted": isHighlighted,
                "ai-search-result-card--pending": isPending,
            })}
            id={elementId}
            role="option"
            tabIndex={0}
            aria-selected={isHighlighted}
            aria-busy={isPending}
            onClick={isPending ? undefined : onClick}
            data-testid="ai-search-result-card"
        >
            <div className="ai-search-result-card-row">
                <div className="ai-search-result-icon" aria-hidden="true">
                    {meta.icon}
                </div>
                <div className="ai-search-result-content">
                    <div className="ai-search-result-top">
                        <span className="ai-search-result-entity">{meta.label}</span>
                    </div>
                    <BTText
                        size="distinct-md"
                        tag="span"
                        className="ai-search-result-title"
                        testid="ai-search-result-title"
                    >
                        {titleContent}
                    </BTText>
                    {subtitle && <div className="ai-search-result-subtitle">{subtitle}</div>}
                    {facets.length > 0 && (
                        <div className="ai-search-result-facets" data-testid="ai-search-facets">
                            {/* A button, not a BTPill: BTPill's actionable mode
                                renders a trailing CLOSE affordance, which reads
                                as "remove" — the opposite of what clicking a
                                facet on a card does. The header's selected
                                chips are the ones that get BTPill. */}
                            {facets.map((facet) => (
                                <BTButton
                                    key={facet.key}
                                    type="tertiary"
                                    className="ai-search-facet"
                                    data-testid="ai-search-facet"
                                    aria-label={`Narrow to ${facet.label}`}
                                    onClick={(e?: React.MouseEvent) => {
                                        // The whole card is a click target;
                                        // narrowing must not also open the record.
                                        e?.stopPropagation();
                                        onFacetClick?.(facet.key);
                                    }}
                                >
                                    {facet.label}
                                </BTButton>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
